// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';

import FloatingBox from '@zapier/design-system/src/components/display/FloatingBox';
import { Colors, Typography } from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import type {
  GetItemPropsReturn,
  getMenuPropsReturn as GetMenuPropsReturn,
} from 'downshift';

import type { FloatingMenuGetLabelForItem, FloatingMenuItem } from './types';

/** @jsx isolateComponent */

export type Props = {
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  ariaLabel: string,
  /**
   * Callback that should be passed to the menu root element. Provided by
   * Downshift.
   */
  getMenuProps: (options: { 'aria-label': string }) => GetMenuPropsReturn,
  /**
   * Callback that should be passed to each menu item. Provided by Downshift.
   */
  getItemProps: (options: { item: FloatingMenuItem }) => GetItemPropsReturn,
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.
   */
  getKeyForItem?: (item: FloatingMenuItem) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: FloatingMenuGetLabelForItem) => string,
  /**
   * The menu item index that should be highlighted. Provided by Downshift.
   */
  highlightedIndex: number,
  /**
   * The list of items to display in the menu.
   */
  items: Array<FloatingMenuItem>,
  /**
   * A render prop which receives a menu `item` and the `getLabelForItem`
   * function and returns a React element, which is an icon illustrating the
   * given menu item. The natural size for the icon is 30x30px. If not
   * provided, no icons will be rendered.
   */
  renderIcon?: (
    item: FloatingMenuItem,
    getLabelForItem: FloatingMenuGetLabelForItem
  ) => Node,
};

const Styles = labelStyles(__filename, {
  list: {
    maxHeight: 190,
    overflow: 'auto',
    padding: 10,
  },

  menuItem: ({ isDisabled, isHighlighted }) => [
    {
      ...Typography.altText900,
      alignItems: 'center',
      borderRadius: 10,
      cursor: 'pointer',
      display: 'flex',
      height: 40,
      padding: '0 10px',
    },

    isDisabled &&
      withModifier('disabled', {
        ...Typography.altText900,
        // TODO: Consider increasing the contrast between disabled items.
        color: Colors.grey700,
      }),

    isHighlighted &&
      withModifier('enabled-highlighted', {
        ...Typography.altText900,
        backgroundColor: Colors.blue100,
        color: Colors.blue500,
        fontWeight: 600,
      }),
  ],

  menuItemIcon: () => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
  ],

  menuItemText: () => [
    {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  ],
});

const FloatingMenu = (props: Props) => {
  const {
    ariaLabel,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    items,
    getKeyForItem,
    getLabelForItem,
  } = props;
  return (
    <FloatingBox align="stretch">
      <ul css={Styles.list} {...getMenuProps({ 'aria-label': ariaLabel })}>
        {items.map((item: FloatingMenuItem, index: number) => (
          <li
            {...getItemProps({ item, disabled: item.disabled })}
            css={Styles.menuItem({
              isDisabled: item.disabled,
              isHighlighted: highlightedIndex === index,
            })}
            key={getKeyForItem ? getKeyForItem(item) : index}
          >
            {props.renderIcon && (
              <span css={Styles.menuItemIcon()}>
                {props.renderIcon(item, props.getLabelForItem)}
              </span>
            )}

            <span css={Styles.menuItemText()}>{getLabelForItem(item)}</span>
          </li>
        ))}
      </ul>
    </FloatingBox>
  );
};

export default FloatingMenu;
